import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ACCEPTED_FILE_TYPES } from '@shared/utils/app-static-data';
import { AddAtpApplicationComponent } from 'app/components/application/atp/add-atp-application/add-atp-application.component';
import { ConfirmationDialogueComponent } from 'app/components/application/common/confirmation-dialogue/confirmation-dialogue.component';
import { AddFirmApplicationComponent } from 'app/components/application/firm/add-firm-application/add-firm-application.component';
import { AddIndividualApplicationComponent } from 'app/components/application/individual/add-individual-application/add-individual-application.component';
import { AddEditAtpAccreditationComponent } from 'app/components/atp/add-edit-atp/add-edit-atp-accreditation/add-edit-atp-accreditation.component';
import { AddEditAtpAuditComponent } from 'app/components/atp/add-edit-atp/add-edit-atp-audit/add-edit-atp-audit.component';
import { AddEditAtpCourseComponent } from 'app/components/atp/add-edit-atp/add-edit-atp-course/add-edit-atp-course.component';
import {
  DocumentEntityType,
  IDocumentFilter,
  IDocumentOwner,
  IDocumentSearchResult
} from 'app/models/document/document';
import { DOCUMENT_CATEGORIES } from 'app/models/document/document-categories';
import { AtpService } from 'app/services/atp/atp.service';
import { SharedService } from 'app/services/core/shared.service';
import { DocumentService } from 'app/services/document/document.service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

/**
 * Documents tab for an ATP, firm or individual. Shows every document reachable from the entity,
 * including those owned by its applications, courses, audits and accreditations. Filtering, sorting
 * and paging all happen on the server.
 */
@Component({
  selector: 'app-entity-documents',
  templateUrl: './entity-documents.component.html',
  styleUrls: ['./entity-documents.component.scss']
})
export class EntityDocumentsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() entityType: DocumentEntityType = 'Atp';
  @Input() entityId: number = 0;
  @Input() cardColor: string = '';

  public tableColumns = ['fileName', 'ownerDisplayName', 'fileExtension', 'fileSize', 'createDate', 'createdBy', 'actions'];
  public tableFormat = [
    { displayName: 'File Name', columnName: 'fileName', type: 'string', size: '26', sortable: true },
    { displayName: 'Related To', columnName: 'ownerDisplayName', type: 'link', size: '28', sortable: false },
    { displayName: 'Type', columnName: 'fileExtension', type: 'string', size: '8', sortable: false },
    { displayName: 'Size', columnName: 'fileSize', type: 'size', size: '8', sortable: true },
    { displayName: 'Uploaded', columnName: 'createDate', type: 'date', size: '10', sortable: true },
    { displayName: 'Uploaded By', columnName: 'createdBy', type: 'string', size: '12', sortable: true }
  ];

  public documents: IDocumentSearchResult[] = [];
  public totalCount = 0;
  public pageSize = 10;
  public pageIndex = 0;
  public loading = false;
  public uploading = false;

  public filterString = '';
  public filterFileTypes = new FormControl([]);
  public filterStartDate: Date = null;
  public filterEndDate: Date = null;
  public sortBy = 'createDate';
  public sortDirection: 'asc' | 'desc' = 'desc';

  public fileTypeOptions: string[] = [];

  public acceptedFileTypes = ACCEPTED_FILE_TYPES.displayText;
  public acceptedFileExtensions = ACCEPTED_FILE_TYPES.extensions.join(',');

  private readonly filterChanged = new Subject<void>();
  private readonly destroyed = new Subject<void>();

  constructor(
    public sharedService: SharedService,
    public dialog: MatDialog,
    private readonly atpService: AtpService,
    private readonly documentService: DocumentService,
    private readonly toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.filterChanged
      .pipe(debounceTime(400), takeUntil(this.destroyed))
      .subscribe(() => this.search(true));

    if (this.entityId > 0) {
      this.loadFilterOptions();
      this.search(true);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entityId'] && !changes['entityId'].firstChange && this.entityId > 0) {
      this.loadFilterOptions();
      this.search(true);
    }
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  get documentCount(): number {
    return this.totalCount;
  }

  get isLoading(): boolean {
    return this.loading;
  }

  /** The file input id has to be unique because all three tabs can be in the DOM at once. */
  get fileInputId(): string {
    return `entityDocumentFileInput_${this.entityType}_${this.entityId}`;
  }

  get entityLabel(): string {
    return this.entityType === 'Person' ? 'individual' : this.entityType.toLowerCase();
  }

  onFilterStringChanged(): void {
    this.filterChanged.next();
  }

  search(resetPage: boolean = false): void {
    if (this.entityId <= 0) {
      return;
    }

    if (resetPage) {
      this.pageIndex = 0;
    }

    this.loading = true;

    this.documentService.searchDocuments(this.buildFilter()).subscribe({
      next: result => {
        this.documents = result.items;
        this.totalCount = result.totalCount;
        this.loading = false;
      },
      error: error => {
        this.toastr.error('Error loading documents: ' + (error.error || error.message));
        this.documents = [];
        this.totalCount = 0;
        this.loading = false;
      }
    });
  }

  resetFilters(): void {
    this.filterString = '';
    this.filterFileTypes.setValue([]);
    this.filterStartDate = null;
    this.filterEndDate = null;
    this.sortBy = 'createDate';
    this.sortDirection = 'desc';
    this.search(true);
  }

  onSortChanged(sort: Sort): void {
    this.sortBy = sort.direction ? sort.active : 'createDate';
    this.sortDirection = (sort.direction || 'desc') as 'asc' | 'desc';
    this.search(true);
  }

  onPageChanged(page: PageEvent): void {
    this.pageIndex = page.pageIndex;
    this.pageSize = page.pageSize;
    this.search();
  }

  /** Audits are the one editor that cannot load itself, so it needs the owning ATP context. */
  canOpenOwner(row: IDocumentSearchResult): boolean {
    if (!row.linkType || !row.linkId) {
      return false;
    }

    return row.linkType !== 'AtpAudit' || this.entityType === 'Atp';
  }

  openOwner(row: IDocumentSearchResult): void {
    if (!this.canOpenOwner(row)) {
      return;
    }

    switch (row.linkType) {
      case 'FirmApplication':
        this.reloadWhenClosed(this.dialog.open(AddFirmApplicationComponent, {
          width: '90%',
          data: { application: { id: row.linkId }, edit: true, firmData: {} },
          autoFocus: false
        }));
        break;

      case 'PersonApplication':
        this.reloadWhenClosed(this.dialog.open(AddIndividualApplicationComponent, {
          width: '90%',
          height: '90vh',
          maxHeight: '90vh',
          data: { application: { id: row.linkId }, edit: true },
          autoFocus: false
        }));
        break;

      case 'AtpApplication':
        this.reloadWhenClosed(this.dialog.open(AddAtpApplicationComponent, this.sharedService.getWizardDialogConfig({
          data: { atpApplicationId: row.linkId }
        })));
        break;

      case 'AtpCourse':
        this.reloadWhenClosed(this.dialog.open(AddEditAtpCourseComponent, {
          width: '90%',
          data: { course: null, atp: null, courseId: row.linkId },
          autoFocus: false
        }));
        break;

      case 'AtpAccreditation':
        this.reloadWhenClosed(this.dialog.open(AddEditAtpAccreditationComponent, {
          width: '70%',
          data: { accredId: row.linkId },
          autoFocus: false
        }));
        break;

      case 'AtpAudit':
        this.openAudit(row.linkId);
        break;
    }
  }

  triggerFileInput(): void {
    (document.getElementById(this.fileInputId) as HTMLInputElement)?.click();
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];

    if (!file) {
      return;
    }

    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!ACCEPTED_FILE_TYPES.extensions.includes(extension)) {
      this.toastr.error(`Only the following file types are accepted: ${ACCEPTED_FILE_TYPES.displayText}`);
      this.clearFileInput();
      return;
    }

    const owner: IDocumentOwner = { ownerType: this.entityType, ownerId: this.entityId };

    this.uploading = true;

    this.documentService.uploadDocument(file, owner, DOCUMENT_CATEGORIES.general).subscribe({
      next: () => {
        this.toastr.success('Document uploaded successfully');
        this.uploading = false;
        this.clearFileInput();
        this.loadFilterOptions();
        this.search(true);
      },
      error: error => {
        this.toastr.error('Upload failed: ' + (error.error || error.message));
        this.uploading = false;
        this.clearFileInput();
      }
    });
  }

  downloadFile(row: IDocumentSearchResult): void {
    this.documentService.downloadDocument(row).subscribe({
      error: error => this.showDownloadError(error)
    });
  }

  deleteFile(row: IDocumentSearchResult): void {
    const dialogRef = this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {
        message: `Are you sure you want to <strong>Delete</strong> the document <strong>${row.fileName}</strong>?`,
        title: 'Delete Document'
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) {
        return;
      }

      this.documentService.deleteDocument(row.id).subscribe({
        next: () => {
          this.toastr.success('Document deleted successfully');
          this.loadFilterOptions();
          this.search();
        },
        error: error => this.toastr.error('Delete failed: ' + (error.error || error.message))
      });
    });
  }

  formatFileSize(bytes: number): string {
    if (!bytes) {
      return '';
    }

    if (bytes < 1024) {
      return `${bytes} B`;
    }

    if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private openAudit(auditId: number): void {
    forkJoin({
      atp: this.atpService.getATPById(this.entityId),
      audits: this.atpService.getATPAudits(this.entityId),
      courses: this.atpService.getATPCourses(this.entityId),
      staff: this.atpService.getATPPeople(this.entityId)
    }).subscribe({
      next: result => {
        const audit = (result.audits ?? []).find(a => a.id === auditId);

        if (!audit) {
          this.toastr.error('The related audit could no longer be found.');
          return;
        }

        this.reloadWhenClosed(this.dialog.open(AddEditAtpAuditComponent, {
          width: '90%',
          autoFocus: false,
          data: { audit, atp: result.atp, courses: result.courses ?? [], staff: result.staff ?? [] }
        }));
      },
      error: error => this.toastr.error('Unable to open the related audit: ' + (error.error || error.message))
    });
  }

  private reloadWhenClosed(dialogRef: MatDialogRef<any>): void {
    dialogRef.afterClosed().subscribe(() => this.search());
  }

  /** Download errors arrive as a Blob because the request asked for one, so the text has to be read out. */
  private showDownloadError(error: any): void {
    const body = error?.error;

    if (body instanceof Blob) {
      body.text().then(text => this.toastr.error('Download failed: ' + (text || error.message)));
      return;
    }

    this.toastr.error('Download failed: ' + (body || error.message));
  }

  private clearFileInput(): void {
    const fileInput = document.getElementById(this.fileInputId) as HTMLInputElement;

    if (fileInput) {
      fileInput.value = '';
    }
  }

  private loadFilterOptions(): void {
    this.documentService.getFilterOptions(this.entityType, this.entityId).subscribe({
      next: options => this.fileTypeOptions = options.fileTypes,
      error: () => this.fileTypeOptions = []
    });
  }

  private buildFilter(): IDocumentFilter {
    return {
      entityType: this.entityType,
      entityId: this.entityId,
      filterString: this.filterString,
      fileTypes: this.filterFileTypes.value ?? [],
      createDateStart: this.filterStartDate ?? null,
      createDateEnd: this.filterEndDate ?? null,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize
    };
  }
}
